import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Badge, Button, Col, Input, Label, Row } from 'reactstrap';
import { attachDocument, getCustodyFileUrl, getCustodyIntervals, getHandoversByChassis, getMasterData, getReturnCases, verifyChain } from '../../devir-teslim/api';
import { downloadTutanakPdf } from '../../devir-teslim/TutanakPdf';
import '../../devir-teslim/devir-teslim.css';

// Chain of custody of the VIN: every handover with parties, damages, photos, signatures and hash links.

const AuthImg = ({ path, alt }) => {
    const [src, setSrc] = useState('');
    useEffect(() => { getCustodyFileUrl(path).then(setSrc).catch(() => setSrc('')); }, [path]);
    return src ? <img src={src} alt={alt} title={alt} onClick={() => window.open(src)} /> : null;
};

// POD document: a photo is shown, a PDF opens in a new tab (both fetched with the auth token)
export const PodDoc = ({ path, label = 'teslim belgesi' }) => {
    const [src, setSrc] = useState('');
    useEffect(() => { getCustodyFileUrl(path).then(setSrc).catch(() => setSrc('')); }, [path]);
    if (!src) return null;
    if (path.startsWith('list:')) {
        return <a href={src} download={`hareket-listesi-${path.slice(5)}.xlsx`}>📄 hareket listesi (Excel)</a>;
    }
    return /\.pdf$/i.test(path)
        ? <a href="#" onClick={(e) => { e.preventDefault(); window.open(src); }}>📄 {label} (PDF)</a>
        : <img src={src} alt="teslim belgesi" title="İmzalı teslim belgesi" onClick={() => window.open(src)} />;
};

// final delivery (proof of delivery): planned vs actual place, the delivery date, the signed delivery note
export const DeliveryBlock = ({ d, documents }) => (
    <div className="ct-pod mt-2">
        <div><Badge color="info">Teslimat (POD)</Badge>{' '}
            {d.matches_plan
                ? <span>Teslim yeri emirdeki varışla aynı: <b>{d.actual_location}</b></span>
                : <span className="text-warning">Planlanan: <b>{d.planned_location || 'emirde varış yok'}</b> · Gerçekleşen: <b>{d.actual_location || '—'}</b></span>}
            {d.actual_place && <span className="dt-muted"> · {d.actual_place}</span>}</div>
        <div className="dt-muted">
            {d.earlier_delivery_date
                ? `Teslim tarihi daha önce girilmiş (${d.earlier_delivery_date}); korunur, bu kayıtla değiştirilmedi.`
                : `Teslim tarihi bu kayıtla yazıldı: ${moment(d.delivered_at).format('DD.MM.YYYY HH:mm')}`}
            {d.groupage_no && ` · grupaj ${d.groupage_no}`}{d.order_ref && ` · emir ${d.order_ref}`}</div>
        {documents && documents.length > 0 && (
            <div className="ct-photos mt-1">{documents.map((x) => <PodDoc key={x.path} path={x.path} />)}
                <div className="dt-muted">Teslim belgesi SHA-256: {documents.map((x) => x.sha256.slice(0, 12) + '…').join(', ')} (kayıt özetine dahil)</div></div>
        )}
    </div>
);

// refused / failed delivery and the return to compound: refusal at the dealer, return scan, investigation, decision
const RETURN_STATUS = { YOLDA: ['Depoya dönüyor', 'info'], DEPODA: ['Depoda – karar bekliyor', 'warning'], KARAR: ['Karar verildi', 'success'] };
const DECISION = { YENIDEN_SEVK: 'Yeniden sevk (yeni sevk emri)', STOK: 'Stoğa alındı', BLOKAJ: 'Blokaj' };
const EVENT = { RET: 'teslim edilemedi', IADE_GIRIS: 'depo girişi (resepsiyon)', NEDEN: 'neden güncellendi', KARAR: 'karar', NOT: 'inceleme notu' };
const ReturnCaseBlock = ({ c }) => (
    <div className="ct-handover">
        <div className="ct-head">
            <div><b>{c.case_no}</b> · {moment(c.opened_at).format('DD.MM.YYYY HH:mm')} · Teslim edilemedi / bayi iadesi</div>
            <div><Badge color={RETURN_STATUS[c.status][1]}>{RETURN_STATUS[c.status][0]}</Badge></div>
        </div>
        <div className="ct-body">
            <div><b>Neden:</b> {c.reason_name || 'araştırılacak'}{c.note ? ` — ${c.note}` : ''}</div>
            <div className="dt-muted">Emirdeki varış: {c.planned_location || '—'}{c.attempted_location ? ` · teslim denenen: ${c.attempted_location}` : ''}
                {c.carrier ? ` · taşıyıcı ${c.carrier}` : ''}{c.groupage_no ? ` · grupaj ${c.groupage_no}` : ''}</div>
            {c.refusal && (
                <div className="mt-1">Bayide ret: {moment(c.refusal.refused_at).format('DD.MM.YYYY HH:mm')} · şoför {c.refusal.driver_person}
                    {c.refusal.receiver && ` · ${c.refusal.receiver}${c.refusal.receiver_person ? ` (${c.refusal.receiver_person})` : ''}`}
                    {c.refusal.receiver_signature ? ' · bayi imzaladı' : ' · bayi imzalamadı'}
                    <div className="ct-photos mt-1">{[c.refusal.driver_signature, c.refusal.receiver_signature, ...c.refusal.photos].filter(Boolean)
                        .map((x) => <PodDoc key={x} path={x} />)}</div></div>
            )}
            {c.returned_at && <div className="dt-muted">Depoya dönüş: {c.return_warehouse} · {moment(c.returned_at).format('DD.MM.YYYY HH:mm')}
                {c.current_place ? ` · şu an ${c.current_place}` : ''}</div>}
            {c.decision && <div><b>Karar:</b> {DECISION[c.decision]} ({moment(c.decided_at).format('DD.MM.YYYY HH:mm')})</div>}
            {c.new_order && <div className="dt-muted">Yeni emir: {c.new_order}</div>}
            {c.blocked && <div className="text-danger">{c.blocked}</div>}
            <div className="mt-1">{c.events.map((e, i) => (
                <div key={i} className="dt-muted">{moment(e.at).format('DD.MM.YYYY HH:mm')} {e.by_name}: {EVENT[e.kind] || e.kind}
                    {e.reason_name ? ` – ${e.reason_name}` : ''}{e.decision ? ` – ${DECISION[e.decision] || e.decision}` : ''}{e.note ? ` (${e.note})` : ''}</div>
            ))}</div>
        </div>
    </div>
);

const TRANSPORT = { GEMI: 'Gemi', TIR: 'Tır', TREN: 'Tren', SURUS: 'Sürüş', DIGER: 'Diğer' };
const SOURCE = { LISTE: ['Liste ile', 'info'], BELGE: ['Belge eki', 'warning'], ENTEGRASYON: ['Entegrasyon', 'info'], MANUEL: ['Elle girildi (belge)', 'warning'] };
const DOC_TYPES = [['RUA_RAPORU', 'RUA hasar raporu'], ['DEVIR_TUTANAGI', 'Devir-teslim tutanağı'], ['CMR', 'CMR / irsaliye'], ['DIGER', 'Diğer belge']];
const emptyLine = { zone_code: '', component_code: '', damage_code: '', size_text: '', note: '' };

// operator document for a step of the chain (e.g. the RUA report of the factory exit reported by list), with the
// damage lines read from it (optional): becomes a "Belge eki" record of its own; the step's record is not changed.
// When there is no step to refer to yet (steps is empty — typically the VIN's first-ever custody record, at a site
// where a terminal cannot be used), the form switches to "manual" mode: the point, locations and parties are chosen
// directly instead of copied from a step, and the record is created with source MANUEL.
const DocumentForm = ({ chassisNo, steps, onSaved, onCancel }) => {
    const manual = steps.length === 0;
    const [md, setMd] = useState(null);
    const [f, setF] = useState({ ref_handover_id: steps[steps.length - 1]?.id || '', doc_type: 'RUA_RAPORU', party_id: '',
        doc_date: moment().format('YYYY-MM-DD'), note: '', point_code: '', from_location_id: 0, to_location_id: 0,
        delivering_party_id: 0, receiving_party_id: 0, event_at: moment().format('YYYY-MM-DD'), accept_warnings: false });
    const [file, setFile] = useState(null);
    const [lines, setLines] = useState([]);
    const [warnings, setWarnings] = useState([]);
    useEffect(() => { getMasterData().then(setMd).catch(() => {}); }, []);
    const setLine = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
    const point = manual ? md?.points.find((p) => p.code === f.point_code) : null;
    const locOptions = (type) => (md?.locations || []).filter((l) => l.is_active && (!type || l.location_type === type));
    const partyOptions = (typeCode) => (md?.parties || []).filter((p) => p.is_active !== false && !p.party_types.includes('BAYI')
        && (!typeCode || p.party_types.includes(typeCode)));
    const save = () => {
        const payload = manual
            ? { ...f, chassis_no: chassisNo, ref_handover_id: 0, party_id: Number(f.party_id),
                from_location_id: Number(f.from_location_id), to_location_id: Number(f.to_location_id),
                delivering_party_id: Number(f.delivering_party_id), receiving_party_id: Number(f.receiving_party_id) }
            : { ...f, chassis_no: chassisNo, ref_handover_id: Number(f.ref_handover_id), party_id: Number(f.party_id) };
        payload.lines = lines.map((l) => ({ line_type: 'HASAR', zone_code: l.zone_code, component_code: l.component_code, damage_code: l.damage_code, size_text: l.size_text, note: l.note }));
        return attachDocument(payload, file)
            .then((r) => { toast.success(`Belge kaydedildi: ${r.handover_no}`); onSaved(); })
            .catch((e) => {
                const w = e?.response?.status === 409 && e.response.data.warnings;
                if (w) { setWarnings(w); return; }
                toast.error(e?.response?.data?.error || 'Kaydedilemedi');
            });
    };
    if (!md) return null;
    const manualReady = manual && f.point_code && f.delivering_party_id && f.receiving_party_id
        && (!point?.from_location_type || f.from_location_id) && (!point?.to_location_type || f.to_location_id);
    const ready = manual ? manualReady : !!f.ref_handover_id;
    return (
        <div className="dt-draft mb-3">
            {manual && (
                <div className="d-flex align-items-center m-0 mb-2 alert alert-info fade show" role="alert">
                    <i className="fa fa-info-circle me-2"></i>Bu araç için henüz devir-teslim kaydı yok. Terminal kullanılamayan bir noktada
                    (sözleşme/erişim kısıtı vb.) alınan belgeyle ilk kaydı burada oluşturabilirsiniz; teslim eden ve teslim alan firma, nokta ve
                    lokasyon seçimi terminaldeki ile aynı kurallara tabidir.
                </div>
            )}
            <Row>
                {manual ? (
                    <>
                        <Col md="3"><Label>Devir-teslim noktası</Label>
                            <Input type="select" value={f.point_code} onChange={(e) => setF({ ...f, point_code: e.target.value, from_location_id: 0, to_location_id: 0 })}>
                                <option value="">— seçiniz —</option>
                                {(md.points || []).filter((p) => p.is_active).map((p) => <option key={p.code} value={p.code}>{p.name_tr}</option>)}</Input></Col>
                        <Col md="3"><Label>Nereden{point?.from_location_type ? '' : ' (opsiyonel)'}</Label>
                            <Input type="select" disabled={!f.point_code} value={f.from_location_id} onChange={(e) => setF({ ...f, from_location_id: Number(e.target.value) })}>
                                <option value={0}>— seçiniz —</option>
                                {locOptions(point?.from_location_type).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</Input></Col>
                        <Col md="3"><Label>Nereye{point?.to_location_type ? '' : ' (opsiyonel)'}</Label>
                            <Input type="select" disabled={!f.point_code} value={f.to_location_id} onChange={(e) => setF({ ...f, to_location_id: Number(e.target.value) })}>
                                <option value={0}>— seçiniz —</option>
                                {locOptions(point?.to_location_type).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</Input></Col>
                        <Col md="3"><Label>Olay tarihi</Label><Input type="date" value={f.event_at} onChange={(e) => setF({ ...f, event_at: e.target.value })} /></Col>
                    </>
                ) : (
                    <Col md="4"><Label>Ait olduğu adım</Label>
                        <Input type="select" value={f.ref_handover_id} onChange={(e) => setF({ ...f, ref_handover_id: e.target.value })}>
                            {steps.map((h) => <option key={h.id} value={h.id}>{h.handover_no} · {h.point_name} · {moment(h.handover_at).format('DD.MM.YYYY HH:mm')}</option>)}
                        </Input></Col>
                )}
                <Col md="3"><Label>Belge tipi</Label>
                    <Input type="select" value={f.doc_type} onChange={(e) => setF({ ...f, doc_type: e.target.value })}>
                        {DOC_TYPES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Input></Col>
                <Col md="3"><Label>Gönderen firma (belgeyi ileten)</Label>
                    <Input type="select" value={f.party_id} onChange={(e) => setF({ ...f, party_id: e.target.value })}>
                        <option value="">— seçiniz —</option>
                        {partyOptions().map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</Input></Col>
                <Col md={manual ? '3' : '2'}><Label>Belge tarihi</Label><Input type="date" value={f.doc_date} onChange={(e) => setF({ ...f, doc_date: e.target.value })} /></Col>
            </Row>
            {manual && (
                <Row className="mt-2">
                    <Col md="4"><Label>Teslim eden firma</Label>
                        <Input type="select" value={f.delivering_party_id} onChange={(e) => setF({ ...f, delivering_party_id: Number(e.target.value) })}>
                            <option value={0}>— seçiniz —</option>
                            {partyOptions(point?.delivering_party_type).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</Input></Col>
                    <Col md="4"><Label>Teslim alan firma</Label>
                        <Input type="select" value={f.receiving_party_id} onChange={(e) => setF({ ...f, receiving_party_id: Number(e.target.value) })}>
                            <option value={0}>— seçiniz —</option>
                            {partyOptions(point?.receiving_party_type).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</Input></Col>
                </Row>
            )}
            <Row className="mt-2">
                <Col md="5"><Label>Dosya (PDF / fotoğraf)</Label><Input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files[0])} /></Col>
                <Col md="7"><Label>Açıklama</Label><Input value={f.note} placeholder="ör. Fabrika çıkışı RUA raporu, saha operatöründen" onChange={(e) => setF({ ...f, note: e.target.value })} /></Col>
            </Row>
            <div className="mt-2"><b>Belgedeki hasarlar</b> <span className="dt-muted">(isteğe bağlı — girilirse açık hasar olarak izlenir)</span></div>
            {lines.map((l, i) => (
                <Row key={i} className="mt-1">
                    <Col md="2"><Input bsSize="sm" type="select" value={l.zone_code} onChange={(e) => setLine(i, 'zone_code', e.target.value)}>
                        <option value="">bölge</option>{md.zones.filter((z) => !z.is_excluded).map((z) => <option key={z.code} value={z.code}>{z.name_tr}</option>)}</Input></Col>
                    <Col md="3"><Input bsSize="sm" type="select" value={l.component_code} onChange={(e) => setLine(i, 'component_code', e.target.value)}>
                        <option value="">parça</option>{md.components.filter((c) => c.is_active !== false).map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name_tr}</option>)}</Input></Col>
                    <Col md="2"><Input bsSize="sm" type="select" value={l.damage_code} onChange={(e) => setLine(i, 'damage_code', e.target.value)}>
                        <option value="">hasar kodu</option>{md.damage_codes.filter((c) => c.is_active !== false).map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name_tr}</option>)}</Input></Col>
                    <Col md="2"><Input bsSize="sm" value={l.size_text} placeholder="boyut" onChange={(e) => setLine(i, 'size_text', e.target.value)} /></Col>
                    <Col md="2"><Input bsSize="sm" value={l.note} placeholder="not" onChange={(e) => setLine(i, 'note', e.target.value)} /></Col>
                    <Col md="1"><Button size="sm" color="light" onClick={() => setLines(lines.filter((_, j) => j !== i))}>✕</Button></Col>
                </Row>
            ))}
            <Button size="sm" color="link" onClick={() => setLines([...lines, { ...emptyLine }])}>+ hasar satırı</Button>
            {warnings.length > 0 && (
                <div className="mt-2">
                    <div className="text-warning">{warnings.join('; ')}</div>
                    <label className="mt-1 mb-0"><input type="checkbox" checked={f.accept_warnings} onChange={(e) => setF({ ...f, accept_warnings: e.target.checked })} /> Uyarıları okudum, onaylıyorum</label>
                </div>
            )}
            <div className="mt-2">
                <Button color="primary" className="mr-1" disabled={!file || !f.party_id || !ready || (warnings.length > 0 && !f.accept_warnings)} onClick={save}>Belgeyi kaydet</Button>
                <Button color="secondary" onClick={onCancel}>Vazgeç</Button>
            </div>
        </div>
    );
};
const fmt = (t) => moment(t).format('DD.MM.YYYY HH:mm');

const DevirTeslim = ({ chassisNo }) => {
    const [list, setList] = useState([]);
    const [check, setCheck] = useState(null);
    const [intervals, setIntervals] = useState([]);
    const [returns, setReturns] = useState([]);
    const [adding, setAdding] = useState(false);
    const [reload, setReload] = useState(0);

    useEffect(() => {
        setCheck(null);
        if (!chassisNo) return setList([]);
        getHandoversByChassis(chassisNo).then(setList).catch(() => setList([]));
        getReturnCases(false, chassisNo).then(setReturns).catch(() => setReturns([]));
        getCustodyIntervals(chassisNo).then(setIntervals).catch(() => setIntervals([]));
    }, [chassisNo, reload]);

    const verify = async () => {
        try { setCheck(await verifyChain(chassisNo)); } catch (e) { toast.error('Doğrulama yapılamadı'); }
    };

    if (!chassisNo) return null;
    const returnBlock = returns.length > 0 && (
        <div className="mb-3">
            <h6>Teslim edilemedi / iade kayıtları ({returns.length})</h6>
            {returns.map((c) => <ReturnCaseBlock key={c.id} c={c} />)}
        </div>
    );
    const noRecords = list.length === 0;

    return (
        <div className="p-2">
            {returnBlock}
            <div className="d-flex align-items-center mb-2">
                {!noRecords && <b className="mr-2">{list.length} devir-teslim kaydı</b>}
                {!noRecords && <Button size="sm" color="primary" outline onClick={verify}>Zinciri doğrula</Button>}
                <Button size="sm" color="primary" outline className="ml-2" onClick={() => setAdding(!adding)}>+ Belge ekle (RUA raporu / tutanak)</Button>
                {check && (
                    <span className="ml-2">
                        <Badge color={check.valid ? 'success' : 'danger'}>{check.valid ? `Zincir geçerli (${check.records} kayıt)` : 'Zincir bozuk'}</Badge>
                        {check.problems.map((p) => <div key={p} className="text-danger small">{p}</div>)}
                    </span>
                )}
            </div>
            {noRecords && !adding && (
                <div className="p-1 dt-muted">Bu araç için devir-teslim kaydı yok (devir-teslim hasar bulunduğunda yapılır;
                    aynı firma içindeki hareketlerde gerekmez). Terminal kullanılamayan bir noktada alınan belgeyle ilk kaydı
                    yukarıdaki "+ Belge ekle" ile oluşturabilirsiniz.</div>
            )}
            {adding && <DocumentForm chassisNo={chassisNo} steps={list.filter((h) => h.source !== 'BELGE')}
                onSaved={() => { setAdding(false); setReload(reload + 1); }} onCancel={() => setAdding(false)} />}
            {!noRecords && (
            <>
            <div className="ct-handover">
                <div className="ct-head"><b>Sorumluluk aralıkları (custody)</b></div>
                <div className="ct-body">
                    <table className="ct-table">
                        <thead><tr><th>Sorumlu taraf</th><th>Başlangıç</th><th>Bitiş</th><th>Lokasyon</th><th>Devrederken bulunan yeni hasar</th></tr></thead>
                        <tbody>{intervals.map((iv) => (
                            <tr key={iv.from_handover}>
                                <td><b>{iv.party}</b></td>
                                <td>{fmt(iv.from_at)} ({iv.from_handover})</td>
                                <td>{iv.to_at ? `${fmt(iv.to_at)} (${iv.to_handover})` : <Badge color="info">Halen sorumlu</Badge>}</td>
                                <td>{iv.location}{iv.gap_party && <div><Badge color="warning">Kayıtsız el değişimi</Badge> sonraki devir-teslimde teslim eden: {iv.gap_party}</div>}</td>
                                <td>{iv.to_at ? (iv.new_damages ? <Badge color="danger">{iv.new_damages}</Badge> : '0') : '—'}</td>
                            </tr>))}</tbody>
                    </table>
                </div>
            </div>
            {list.map((h, hi) => {
                const damages = h.lines.filter((l) => l.line_type === 'HASAR');
                const notes = h.lines.filter((l) => l.line_type !== 'HASAR');
                return (
                    <div key={h.id} className="ct-handover">
                        <div className="ct-head">
                            <div><b>{h.handover_no}</b> · {moment(h.handover_at).format('DD.MM.YYYY HH:mm')} · {h.point_name} · {h.location_name}
                                {SOURCE[h.source] && <>{' '}<Badge color={SOURCE[h.source][1]}>{SOURCE[h.source][0]}{h.ref_handover_no ? ` · ${h.ref_handover_no}` : ''}</Badge></>}</div>
                            <div>
                                <Badge color={h.result === 'HASARLI' ? 'danger' : 'success'}>{h.result === 'HASARLI' ? `HASARLI (${damages.length})` : 'HASARSIZ'}</Badge>{' '}
                                <Button size="sm" color="light" onClick={() => downloadTutanakPdf(h.id).catch(() => toast.error('PDF oluşturulamadı'))}>Tutanak PDF</Button>
                            </div>
                        </div>
                        <div className="ct-body">
                            <div><b>Teslim eden:</b> {h.delivering_party} ({h.delivering_person}) → <b>Teslim alan:</b> {h.receiving_party} ({h.receiving_person})</div>
                            <div className="dt-muted">Taşıma: {TRANSPORT[h.transport_mode] || h.transport_mode} {h.transport_ref || ''}{h.document_ref ? ` · Belge: ${h.document_ref}` : ''}</div>
                            {(h.from_location_name || h.flow) && (
                                <div className="dt-muted">{h.from_location_name && `${h.from_location_name} → ${h.to_location_name || '—'}`}
                                    {h.flow && ` · akış: ${{ IHRACAT: 'ihracat', ITHALAT: 'ithalat', YERLI: 'yerli' }[h.flow] || h.flow}`}
                                    {h.order_ref && ` · emir: ${h.order_ref}`}{h.customs_status && ` · ${h.customs_status}`}</div>
                            )}
                            {h.warnings && h.warnings.length > 0 && <div className="text-warning">Onaylanan uyarılar: {h.warnings.join('; ')}</div>}
                            {damages.length > 0 && (
                                <table className="ct-table mt-2">
                                    <thead><tr><th>#</th><th>Bölge</th><th>Parça</th><th>Hasar</th><th>Boyut / açıklama</th><th>Kayıt</th><th>Sorumlu</th><th>Durum</th><th>Fotoğraf</th></tr></thead>
                                    <tbody>{damages.map((l) => (
                                        <tr key={l.id}>
                                            <td>{l.line_no}</td><td>{l.zone_name}</td>
                                            <td><b>{l.component_code}</b> {l.component_name}</td>
                                            <td><b>{l.damage_code}</b> {l.damage_name}</td>
                                            <td>{l.size_text}{l.note ? <div className="dt-muted">{l.note}</div> : null}</td>
                                            <td>{l.is_new ? <Badge color="danger">Yeni</Badge> : <><Badge color="secondary">Önceden kayıtlı</Badge><div className="dt-muted">ilk tespit: {l.first_handover_no}</div></>}</td>
                                            <td>{l.responsible_party}
                                                {l.is_new && h.source === 'BELGE' && <div className="dt-muted">adım: {h.ref_handover_no} (belgeden)</div>}
                                                {l.is_new && h.source !== 'BELGE' && <div className="dt-muted">aralık: {hi > 0 ? `${fmt(list[hi - 1].handover_at)} – ${fmt(h.handover_at)}` : `sistem kaydı öncesi – ${fmt(h.handover_at)}`}</div>}</td>
                                            <td>{l.is_new ? l.status_name : '—'}
                                                {l.history.map((x, i) => <div key={i} className="dt-muted">{fmt(x.changed_at)} {x.changed_by_name}: {x.to_status_name}{x.note ? ` (${x.note})` : ''}</div>)}</td>
                                            <td className="ct-photos">{l.photos.map((p) => <AuthImg key={p.path} path={p.path}
                                                alt={`${moment(p.uploaded_at).format('DD.MM.YYYY HH:mm:ss')} · SHA-256 ${p.sha256.slice(0, 12)}…`} />)}</td>
                                        </tr>))}</tbody>
                                </table>
                            )}
                            {notes.map((l) => (
                                <div key={l.id} className="mt-1"><Badge color={l.line_type === 'CEKINCE' ? 'warning' : 'info'}>{l.line_type === 'CEKINCE' ? 'Çekince' : 'Gözlem'}</Badge>{' '}
                                    {l.observation_name}{l.note ? ` — ${l.note}` : ''}</div>
                            ))}
                            {h.delivery && <DeliveryBlock d={h.delivery} documents={h.documents} />}
                            {h.source === 'LISTE' && (
                                <div className="mt-2"><span className="dt-muted">Kayıt kaynağı: {h.point_code === 'DEVRALMA' ? 'devralma (açılış stoku) listesi' : 'saha operatörünün hareket listesi'} (imza yerine dosya, SHA-256 {h.delivering_signature_sha256.slice(0, 12)}…) </span>
                                    <PodDoc path={h.delivering_signature} /></div>
                            )}
                            {h.source === 'BELGE' && (
                                <div className="mt-2"><span className="dt-muted">{h.ref_handover_no} adımına eklenen belge: </span>
                                    {h.documents.map((d) => <PodDoc key={d.path} path={d.path} label="belge" />)}</div>
                            )}
                            {h.source === 'MANUEL' && (
                                <div className="mt-2"><span className="dt-muted">Terminal kullanılmadan, belgeyle elle oluşturuldu (imza yerine belge): </span>
                                    {h.documents.map((d) => <PodDoc key={d.path} path={d.path} label="belge" />)}</div>
                            )}
                            {(!h.source || h.source === 'TERMINAL') && (
                                <div className="d-flex mt-2 ct-sig">
                                    <div className="mr-3"><div className="dt-muted">Teslim eden imza</div><AuthImg path={h.delivering_signature} alt="Teslim eden imza" /></div>
                                    <div><div className="dt-muted">Teslim alan imza</div><AuthImg path={h.receiving_signature} alt="Teslim alan imza" /></div>
                                </div>
                            )}
                            <div className="ct-hash mt-2">Kayıt: {h.record_hash}<br />Önceki: {h.prev_hash}</div>
                        </div>
                    </div>
                );
            })}
            </>
            )}
        </div>
    );
};

export default DevirTeslim;
